import { useCallback, useMemo } from 'react';
import { usePipelinePresence } from '@/hooks/use-pipeline-presence';
import { usePipelineContext } from '../../context';
import { PipelineStageHeader } from './PipelineStageHeader';
import { PipelineStageHeaderCollapsed } from './PipelineStageHeaderCollapsed';
import { PipelineStageContent } from './PipelineStageContent';
import type { PipelineStage as PipelineStageType } from '../../types';

// Co-located styles for this component
import './PipelineStage.css';

interface PipelineStageProps {
  stage: PipelineStageType;
}

/**
 * Pipeline Stage - Container for a single stage column
 * Uses fixed widths (320px expanded, 48px collapsed) with flexShrink: 0
 * Handles collapse/expand state, presence, and card rendering
 */
export function PipelineStage({ stage }: PipelineStageProps) {
  const { state, actions, meta } = usePipelineContext();
  const { presence: pipelinePresence, currentUserId } = usePipelinePresence();

  const isCollapsed = state.collapsedStages.has(stage.id);
  const stageCustomers = meta.getCustomersByStage(stage.id);
  const searchActive = meta.searchActive;
  const stageMatchCount = meta.stageMatchCounts.get(stage.id) ?? 0;
  const hasSearchMatches = searchActive && stageMatchCount > 0;
  const shouldDimStage = searchActive && stageMatchCount === 0;

  // Calculate stage value
  const stageValue = useMemo(
    () =>
      stageCustomers.reduce((sum, customer) => sum + (customer.dealValue || 0), 0) /
      100,
    [stageCustomers]
  );

  // Get presence for this stage
  const stagePresence = useMemo(
    () =>
      (pipelinePresence.stages?.[stage.id] ?? []).filter(
        (person) => person.userId !== currentUserId
      ),
    [pipelinePresence.stages, stage.id, currentUserId]
  );

  // Handle toggle collapse
  const handleToggleCollapse = useCallback(() => {
    actions.toggleStageCollapse(stage, stageCustomers.length);
  }, [actions, stage, stageCustomers.length]);

  // Register stage section ref
  const registerStageSectionRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (node) {
        meta.refs.stageSectionRefs.current.set(stage.id, node);
      } else {
        meta.refs.stageSectionRefs.current.delete(stage.id);
      }
    },
    [meta.refs, stage.id]
  );

  return (
    <div
      ref={registerStageSectionRef}
      className={`pipeline-stage-container flex flex-col ${
        isCollapsed ? 'pipeline-stage-collapsed' : 'pipeline-stage-expanded'
      }`}
      data-stage-id={stage.id}
      data-collapsed={isCollapsed ? 'true' : 'false'}
      style={{
        height: '100%',
        overflow: 'hidden',
      }}
    >
      {/* Stage Header - HubSpot-style gradient background */}
      <div
        className={`stage-header-modern flex items-center justify-between rounded-t-xl shadow-md cursor-pointer hover:shadow-lg flex-shrink-0 transition-shadow duration-200 ${
          isCollapsed ? 'rounded-xl p-3' : 'p-0'
        } ${hasSearchMatches ? 'stage-header-match' : ''} ${
          shouldDimStage ? 'opacity-50' : ''
        }`}
        style={{
          // CSS custom property for child components to inherit
          '--stage-color': stage.color,
        } as React.CSSProperties}
        role="button"
        tabIndex={0}
        aria-expanded={!isCollapsed}
        aria-controls={`stage-panel-${stage.id}`}
        onClick={handleToggleCollapse}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleToggleCollapse();
          }
        }}
      >
        {!isCollapsed ? (
          <PipelineStageHeader
            stage={stage}
            customerCount={stageCustomers.length}
            stageValue={stageValue}
            matchCount={stageMatchCount}
            hasSearchMatches={hasSearchMatches}
            shouldDimStage={shouldDimStage}
            stagePresence={stagePresence}
            isCollapsed={isCollapsed}
            onToggleCollapse={handleToggleCollapse}
          />
        ) : (
          <PipelineStageHeaderCollapsed
            stage={stage}
            customers={stageCustomers}
            stageValue={stageValue}
            hasSearchMatches={hasSearchMatches}
            shouldDimStage={shouldDimStage}
            onToggleCollapse={handleToggleCollapse}
          />
        )}
      </div>

      {/* Stage Content - fills remaining height */}
      {!isCollapsed && (
        <div className="flex-1 min-h-0 transition-opacity duration-300 ease-out">
          <PipelineStageContent stage={stage} customers={stageCustomers} />
        </div>
      )}
    </div>
  );
}
